import { useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Measures the real height of the fixed app header (the MUI AppBar).
 *
 * The header height is NOT constant: extra rows are stacked inside the AppBar
 * depending on the page — the settings sub-navigation, the status links, and
 * the "FluidIT Driver" announcement bar (shown on every page except "/"). It
 * also changes with responsive breakpoints. A hardcoded offset therefore lets
 * the header overlap the page content whenever an extra row appears.
 *
 * Returns the header's bottom edge in px, re-measured on navigation and kept in
 * sync via ResizeObserver + window resize. Returns null until first measured,
 * so callers can fall back to a static offset.
 *
 * Must be used inside a <Router> (relies on useLocation).
 */
export default function useAppHeaderHeight() {
  const { pathname } = useLocation();
  const [height, setHeight] = useState(null);

  useLayoutEffect(() => {
    const header =
      document.querySelector(".MuiAppBar-root") ||
      document.querySelector("header") ||
      document.querySelector("nav");
    if (!header) return undefined;

    const measure = () => setHeight(header.getBoundingClientRect().bottom);
    measure();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(header);
    }
    window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [pathname]);

  return height;
}
